import { FilterCard, CustomDataTable, useApi, Toaster, CustomAddFeeStageAreaModal, CustomBackdrop, validationMessages } from '../../utils/globalImports';
import { React, useState, useEffect, Box } from '../../utils/globalImports';
import { ApiResponse } from '../../services/apiService';
import { useConfirmCloseDialog } from '../../contexts/ConfirmCloseDialogContext';

interface FeeStage {
    feeStageAreaNameId?: number;
    feeStageAreaName: string;
    status: boolean;
    description: string;
    createdId?: number;
    updatedId?: number;
    createdAt?: Date;
    updatedAt?: Date;
}

const FeeStageArea: React.FC = () => {

    // State to manage the modal visibility
    const [modalOpen, setModalOpen] = useState(false);
    const [feeDatalist, setFeeDatalist] = useState<FeeStage[]>([]);
    const [selectedFee, setSelectedFee] = useState<any | null>(null);
    const [feeData, setFeeData] = useState<any[]>([]);
    const [filteredData, setFilteredData] = useState<any[]>([]);
    const [currentStatus, setCurrentStatus] = useState<string[]>(['Active']);
    const [toast, setToast] = useState({ open: false, message: "", type: "success" });
    const [isOn, setIsOn] = useState<boolean>();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

    const { execute, loading } = useApi();
    const { showConfirmCloseDialog } = useConfirmCloseDialog();
    const props = { showClientDropdown: false, title: false, actionText: 'Add Fee Stage Area', searchbarText: 'Search for a fee stage area by name, description' };

    const userId = Number(localStorage.getItem("userId"));

    useEffect(() => {
        fetchFees();
    }, []);  // The empty dependency array ensures it only runs once when the component mounts

    // Fetch fees
    const fetchFees = async () => {
        const { data } = await execute<FeeStage[]>('get', '/Lookup/GetFeeStageAreaNames');

        if (data) {
            setFeeDatalist(data);

            const feeData = data.map((fee) => {
                const { feeStageAreaNameId, createdAt, feeStageAreaName, description, status } = fee;
                const Status = status ? 'Active' : 'Inactive';
                return {
                    id: feeStageAreaNameId,
                    createdAt,
                    feeStageAreaNameId,
                    Name: feeStageAreaName,
                    description: description,
                    Status
                };
            });

            setFeeData(feeData);

            const activeFees = feeData
                .filter((fee) => currentStatus.includes(fee.Status))
                .sort((a, b) => a.Name.localeCompare(b.Name, undefined, { sensitivity: 'base' }));

            setFilteredData(activeFees);
        }
    };

    // Filter Data
    const handleSearchGridData = (query: string, status: Number) => {
        const numericStatus = Number(status); // Convert status to a number
        const lowerCaseQuery = query.toLowerCase();

        // Map statuses to numeric values for filtering
        const statusMapping: Record<string, number> = {
            Active: 1,
            Inactive: 0,
        };

        // Ensure that feeData is properly filtered
        const filtered = feeData.filter((item) => {
            const itemStatus = statusMapping[item.Status] ?? -1; // Ensure mapping exists
            const matchesQuery =
                item.Name.toLowerCase().includes(lowerCaseQuery) || //Search in feeName
                (item.description && item.description.toLowerCase().includes(lowerCaseQuery)); //Search in description

            const matchesStatus =
                numericStatus === -1 || // If -1 (show all)
                itemStatus === numericStatus; // Match the exact status

            return matchesQuery && matchesStatus;
        }).sort((a, b) =>
            a.Name.localeCompare(b.Name, undefined, { sensitivity: 'base' })
        );

        //Update filteredData with correctly filtered fees
        setFilteredData(filtered);

        //Ensure currentStatus is correctly updated
        setCurrentStatus(
            numericStatus === 0
                ? ['Inactive']
                : numericStatus === -1
                    ? ['Active', 'Inactive']
                    : numericStatus === 1
                        ? ['Active']
                        : currentStatus
        );
    };

    const handleFormSubmit = async (formData: any, setBackendErrors?: (errors: any) => void) => {
        let response: ApiResponse<any> = { data: null, error: null };
        if (selectedFee) {
            const updatedFee = feeDatalist.find((fee) => fee.feeStageAreaNameId === selectedFee.feeStageAreaNameId);
            // If the fee is found, update the fields
            if (updatedFee) {
                // Step 2: Update the fields on the filtered record
                const updatedFeeData = {
                    ...updatedFee, // Keep the original fee data
                    feeStageAreaName: formData.feeName,
                    description: formData.description,
                    status: formData.status === 'Active' ? true : false, // Update based on Status
                    updatedId: userId,
                };

                response = await execute('put', '/Lookup/UpdateFeeStageAreaName', updatedFeeData);
            }
        } else {

            // Adding a new fee
            const fee: FeeStage = {
                feeStageAreaName: formData.feeName,
                description: formData.description,
                status: formData.status === 'Active' ? true : false,
                createdId: userId,
            };

            response = await execute('post', '/Lookup/CreateFeeStageAreaName', fee);
        }

        if (response && response.error) {
            if (
                typeof response.error === 'string' &&
                response.error.includes('Fee stage area name already exists')
            ) {
                setBackendErrors?.({ name: response.error });
                return false; // Don't close modal
            } else {
                setToast({ open: true, message: response.error, type: 'error' });
                return false; // Don't close modal on other errors
            }
        } else {
            // Submission successful – close the modal
            setToast({ open: true, message: response?.data, type: 'success' });
            setModalOpen(false);
            setSelectedFee(null);
            handleContentChange(false);
            fetchFees();
            return true;
        }
    };

    const handleEdit = (fee: any) => {
        setSelectedFee({
            ...fee,
            feeName: fee.Name,
            status: fee.Status === 'Active' ? 'Active' : 'Inactive'
        });
        handleOpenModal(); // Open the modal
    };

    const handleAdd = () => {
        setSelectedFee(null);
        setModalOpen(true);
    };

    const handleOpenModal = () => {
        setModalOpen(true);
    };

    const handleToggleState = (row: any) => {
        if (row.Status === 'Inactive') {
            setIsOn(false)
        } else {
            setIsOn(true)
        }
        setSelectedFee(row);
        setDialogOpen(true);
    };

    const handleDialogCancel = () => {
        setDialogOpen(false);
        setSelectedFee(null);
    };

    const handleDialogConfirm = async () => {
        if (!selectedFee) return;
        try {
            // Find the selected fee in the list
            const updatedFee = feeDatalist.find(
                (fee) => fee.feeStageAreaNameId === selectedFee.feeStageAreaNameId
            );

            if (updatedFee) {
                //Toggle the status dynamically
                const newStatus = updatedFee.status ? false : true;

                const updatedFeeData = {
                    ...updatedFee,
                    status: newStatus, //Toggle between true (Active) and false (Inactive)
                    updatedId: userId,
                };

                //Make the API call to update the status
                const response = await execute('put', '/Lookup/UpdateFeeStageAreaName', updatedFeeData);
                if (response?.error) {
                    setToast({ open: true, message: response.error, type: 'error' });
                } else {
                    setToast({ open: true, message: 'Status updated successfully', type: 'success' });
                }
            }

            //Refresh the fee list
            fetchFees();
            setSelectedFee(null);
        } catch (error) {
            console.error('Error updating status:', error);
        } finally {
            setDialogOpen(false);
        }
    };

    const handleContentChange = (state: boolean) => {
        setHasUnsavedChanges(state);
    };

    const handleClose = () => {
        if (hasUnsavedChanges) {
            showConfirmCloseDialog({
                onConfirm: () => {
                    setHasUnsavedChanges(false);
                    setModalOpen(false);
                },
                onCancel: () => {
                },
            });
        }
        else {
            setModalOpen(false);
        }
    }

    return (
        <>
            <FilterCard {...props} handleModalOrNavigate={handleAdd} handleSearch={handleSearchGridData} />
            <CustomAddFeeStageAreaModal handleContentChange={handleContentChange} open={modalOpen} onClose={handleClose} onSubmit={handleFormSubmit} feeData={selectedFee} />
            {feeDatalist.length > 0 && (
                <CustomDataTable
                    rows={filteredData}
                    actionText="Fee Stage Area"
                    sortOptions={[{ value: "Name", text: "Name" }]}
                    onRowEdit={handleEdit}
                    onToggleClick={handleToggleState}
                    isDialogOpen={dialogOpen}
                    isToggleOn={isOn}
                    handleCancel={handleDialogCancel}
                    handleConfirm={handleDialogConfirm}
                    // gridHeight={'calc(100vh - 500px)'}
                    noResultMessage={validationMessages.common_RecordNotFound("fee stage areas")}
                />
            )}
            <CustomBackdrop open={loading} />
            <Toaster open={toast.open} message={toast.message || ""} type={toast.type as "success" | "error"} onClose={() => setToast({ ...toast, open: false })} />
        </>
    );
};

export default FeeStageArea;